<script>
  import IconSearch from '@/components/IconSearch.vue';
  import { debounce, whenFastSimonReady, handleViewportSize } from '@/lib/vue-helpers.js';
  export default {
    name: 'LiveSearch',
    components: {
      IconSearch
    },
    data() {
      return {
        currencyCode: window.Shopify.currency.active,
        searchResults: [],
        placeHolder: 'Search',
        searchInput: '',
        defaultQuery: '',
        defaultQueryEnabled: false,
        defaultSuggestions: '',
        defaultCategories: '',
        suggestions: [],
        categories: [],
        categoryHandles: [],
        showCrosssellPercentage: false,
        defaultResults: null,
        isInputFocused: false,
        isMobile: window.innerWidth <= window.theme?.sizes?.medium,
        isNavigating: false,
        searchRequestId: 0
      };
    },
    mounted() {
      this.handleViewportSize = handleViewportSize(this);
      this.debouncedHideMobile = debounce(this.shouldHideMobile);
      this.debouncedCheckAnnouncement = debounce(this.checkForAnnouncementBar);
      this.debouncedOnChanger = debounce(this.onChanger, 200, false);
      this.defaultQuery = this.$el.parentElement.dataset.defaultQuery;
      this.defaultQueryEnabled = this.$el.parentElement.dataset.defaultQueryEnabled === 'true';
      this.defaultSuggestions = this.$el.parentElement.dataset.defaultSuggestions;
      this.suggestions = this.defaultQueryEnabled && this.defaultSuggestions ? this.defaultSuggestions.split(',') : this.searchResults.popularSearches;
      this.defaultCategories = this.$el.parentElement.dataset.defaultCategories;
      const titlesJson = this.$el.parentElement.dataset.defaultCategoryTitles;
      const handlesJson = this.$el.parentElement.dataset.defaultCategoryHandles;

      if (this.defaultQueryEnabled && titlesJson && handlesJson) {
        try {
          this.categories = JSON.parse(titlesJson);
          this.categoryHandles = JSON.parse(handlesJson);
        } catch (e) {
          this.categories = (this.defaultCategories || '').split(',').filter(Boolean);
          this.categoryHandles = this.categories.slice();
        }
      } else {
        this.categories = this.defaultQueryEnabled ? this.defaultCategories.split(',').filter(Boolean) : this.searchResults.categories || [];
        this.categoryHandles = this.categories.slice();
      }

      this.loadHandler = () => {
        window.addEventListener('scroll', this.debouncedCheckAnnouncement);
        this.debouncedCheckAnnouncement();
      };

      window.addEventListener('scroll', this.debouncedHideMobile);
      window.addEventListener('load', this.loadHandler);
      this.onResize = () => {
        this.isMobile = window.innerWidth <= window.theme?.sizes?.medium;
      };
      document.addEventListener('theme:resize:width', this.onResize);
      this.onPageShow = e => {
        if (e.persisted) {
          this.isNavigating = false;
          this.searchRequestId++;
          this.defaultResults = null;
        }
      };
      window.addEventListener('pageshow', this.onPageShow);

      this.onPopdownClose = e => {
        if (e.target && e.target.contains && e.target.contains(this.$el)) {
          this.closeSearchResults();
        }
      };
      document.addEventListener('theme:scroll:unlock', this.onPopdownClose);

      // The popdown's programmatic focus() fires no focus event while the document lacks focus, so onFocus can't be relied on to load defaults.
      // Drawers and modals also dispatch theme:scroll:lock on document, so only react to this instance's own popdown.
      this.onPopdownOpen = e => {
        if (e.target !== this.$el.closest('[data-popdown-outer]')) return;
        this.isInputFocused = true;
        this.isNavigating = false;
        if (!this.searchInput) this.loadDefaultResults();
      };
      document.addEventListener('theme:scroll:lock', this.onPopdownOpen);

      this.checkDirectSearchNavigation();
      this.showCrosssellPercentage = window.siteGlobals?.crosssell_show_percentage_off === 'true';
    },
    beforeUnmount() {
      if (this.handleViewportSize) this.handleViewportSize();
      window.removeEventListener('scroll', this.debouncedHideMobile);
      window.removeEventListener('scroll', this.debouncedCheckAnnouncement);
      window.removeEventListener('load', this.loadHandler);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('theme:resize:width', this.onResize);
      document.removeEventListener('theme:scroll:unlock', this.onPopdownClose);
      document.removeEventListener('theme:scroll:lock', this.onPopdownOpen);
    },
    computed: {
      getCurrentLocales() {
        let root = window.Shopify.routes.root;
        return root.endsWith('/') ? root.slice(0, -1) : root;
      },
      mobileSearchSeeAll() {
        const query = this.escapeHtml(this.searchInput.trim());
        return `See All Results for "<strong>${query}</strong>" >`;
      }
    },
    methods: {
      escapeHtml(text) {
        if (!text) return '';
        return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      },
      resetToDefaultSearchResults() {
        if (!this.defaultQueryEnabled) {
          this.suggestions = [];
          this.categories = [];
          return;
        }
        this.suggestions = this.defaultSuggestions && this.defaultSuggestions.length ? this.defaultSuggestions.split(',').filter(Boolean) : [];

        const titlesJson = this.$el.parentElement.dataset.defaultCategoryTitles;
        const handlesJson = this.$el.parentElement.dataset.defaultCategoryHandles;
        if (titlesJson && handlesJson) {
          try {
            this.categories = JSON.parse(titlesJson);
            this.categoryHandles = JSON.parse(handlesJson);
          } catch (e) {
            this.categories = (this.defaultCategories || '').split(',').filter(Boolean);
            this.categoryHandles = this.categories.slice();
          }
        } else {
          this.categories = (this.defaultCategories || '').split(',').filter(Boolean);
          this.categoryHandles = this.categories.slice();
        }
      },
      loadDefaultResults() {
        if (!this.defaultQueryEnabled) return;
        this.resetToDefaultSearchResults();
        const requestId = this.searchRequestId;
        if (this.defaultResults) {
          this.searchResults = this.defaultResults;
          return;
        }
        whenFastSimonReady(() => {
          window.FastSimonSDK.instantSearch({
            query: this.defaultQuery,
            callback: response => {
              if (!response?.payload) return;
              if (response.payload.query !== this.defaultQuery.trim()) return;

              this.defaultResults = response.payload;

              if (this.searchInput || requestId !== this.searchRequestId) return;
              this.searchResults = response.payload;
            }
          });
        });
      },
      checkForAnnouncementBar() {
        let announcementBar = document.querySelector('.announcement__wrapper');
        let searchPopdown = document.querySelector('.search-popdown');
        if (announcementBar && window.scrollY < 33) {
          searchPopdown.style.top = announcementBar.clientHeight + 'px';
        } else {
          searchPopdown.style.top = 0;
        }
      },
      getSearchQuery() {
        let urlParams = new URLSearchParams(window.location.search);
        let sQuery = '';
        if (urlParams.get('q')) {
          sQuery = urlParams.get('q');
        }
        return sQuery;
      },
      setTurbolinkCollectionFlag(url) {
        if (url.includes('/collections/')) {
          localStorage.setItem('fromTurboLink', 'true');
        }
      },
      cancelPendingSearch() {
        this.debouncedOnChanger?.cancel();
      },
      checkDirectSearchNavigation() {
        const searchQuery = this.getSearchQuery();
        if (searchQuery && window.location.pathname.includes('/pages/search-results')) {
          this.cancelPendingSearch();
          this.resolveTurbolink(searchQuery, false);
        }
      },
      syncInputValue() {
        const el = this.$refs.searchInput;
        if (el && el.value !== this.searchInput) this.searchInput = el.value;
      },
      onChanger() {
        if (this.isNavigating) return;
        this.syncInputValue();

        if (!this.searchInput) {
          this.searchRequestId++;
          this.searchResults = [];
          if (this.defaultQueryEnabled) {
            this.loadDefaultResults();
          } else {
            this.suggestions = [];
            this.categories = [];
          }
          return;
        }

        const requestId = ++this.searchRequestId;
        const query = this.searchInput.trim();
        whenFastSimonReady(() => {
          if (requestId !== this.searchRequestId) return;
          window.FastSimonSDK.instantSearch({
            query: query,
            callback: response => {
              if (requestId !== this.searchRequestId) return;
              if (!response?.payload) return;

              this.searchResults = response.payload;

              this.suggestions = response.payload.popularSearches || [];
              this.categories = response.payload.categories || [];
            }
          });
        });
      },
      resolveTurbolink(term, fallbackToSrp) {
        const phrase = term.trim();
        if (!phrase) return;
        let done = false;
        const navigateToSrpIfNeeded = () => {
          if (done) return;
          if (!fallbackToSrp) return;
          done = true;
          window.location = this.getSearchResultsUrl(phrase);
        };
        whenFastSimonReady(
          () => {
            // Turbolink lives in payload.turbolink, not action; callback may fire twice before final pass.
            const srpTimer = setTimeout(navigateToSrpIfNeeded, 4000);
            window.FastSimonSDK.fullTextSearch({
              term: phrase,
              callback: response => {
                if (done) return;
                const turbolinkUrl = response?.payload?.turbolink?.u;
                if (turbolinkUrl) {
                  clearTimeout(srpTimer);
                  done = true;
                  this.setTurbolinkCollectionFlag(turbolinkUrl);
                  window.location.href = turbolinkUrl;
                  return;
                }
                if (response?.action === 'facets and products') {
                  clearTimeout(srpTimer);
                  navigateToSrpIfNeeded();
                }
              }
            });
          },
          { onTimeout: navigateToSrpIfNeeded }
        );
      },
      submitSearch(e, searchTerm) {
        if (e?.preventDefault) e.preventDefault();
        if (e?.stopPropagation) e.stopPropagation();
        this.navigateToSearchResults(searchTerm);
      },
      navigateToSearchResults(searchTerm) {
        const searchPhrase = (searchTerm != null ? String(searchTerm) : this.searchInput).trim();
        if (!searchPhrase) return;
        this.cancelPendingSearch();
        this.isNavigating = true;
        this.resolveTurbolink(searchPhrase, true);
      },
      getSearchResultsUrl(searchTerm) {
        return this.getCurrentLocales + '/pages/search-results?q=' + encodeURIComponent(String(searchTerm).trim());
      },
      getSearchLink(searchTerm) {
        return this.getSearchResultsUrl(searchTerm);
      },
      getCategoryDisplayLabel(category, categoryIndex) {
        if (typeof category === 'string') {
          const label = category || '';
          // Only rewrite when the display string is the handle (fallback path); keep real titles intact.
          if (label && label === (this.categoryHandles[categoryIndex] || '')) {
            return label.replace(/-/g, ' ');
          }
          return label;
        }
        if (category && category.l) {
          return category.l;
        }
        return category || '';
      },
      trackFastSimon(product) {
        whenFastSimonReady(() => {
          const fromTurboLink = localStorage.getItem('fromTurboLink');
          if (fromTurboLink) {
            localStorage.removeItem('fromTurboLink');
          }

          window.FastSimonSDK.event({
            eventName: window.FastSimonEventName.AutocompleteProductClicked,
            data: {
              query: this.searchInput,
              productID: product.id,
              productQuery: product.s,
              fromTurboLink: fromTurboLink === 'true'
            }
          });
        });
      },
      setPriceDisplay(name, compare, price) {
        let html = '<span class="search-flydown--product-title">' + name + '</span><br />';
        const hasCompareAt = compare != '' && compare != '0.00';
        if (hasCompareAt) {
          html = html + '<span class="price on-sale">' + this.convertToCurrency(price) + '</span>';

          html = html + '<span class="compare-at gt-p__small">' + this.convertToCurrency(compare) + '</span>';

          if (this.showCrosssellPercentage) {
            const percentOff = Math.floor(((compare - price) / compare) * 100);
            html = html + '<small class="gt-p__small--x"><span class="percentage-off on-sale">(' + percentOff + '% off)</span></small>';
          }
        } else {
          html = html + '<span class="price">' + this.convertToCurrency(price) + '</span>';
        }
        return html;
      },
      convertToCurrency(str) {
        let dollarUS = Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.currencyCode
        });
        let price = dollarUS.format(str).replace('.00', '');
        if (price.includes('CA')) {
          price = price.replace('CA', '');
          price = price + ' CAD';
        }
        return price;
      },
      clearSearchResults() {
        this.searchInput = '';
        this.searchRequestId++;
        if (this.defaultQueryEnabled) {
          this.searchResults = [];
          this.loadDefaultResults();
        } else {
          this.isInputFocused = true;
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
          this.loadDefaultResults();
        }
      },
      shouldHideMobile() {
        const search = document.getElementById('live-search');
        if (window.scrollY && search) {
          if (window.scrollY > 200) {
            search.classList.add('mobile-hide');
          } else {
            search.classList.remove('mobile-hide');
          }
        }
      },
      closeSearchResults() {
        this.searchInput = '';
        this.searchRequestId++;
        this.searchResults = [];
        this.resetToDefaultSearchResults();
      },
      onTouchStart() {
        this.$refs.searchInput.focus();
      },
      onInputClick() {
        this.$refs.searchInput.focus();
      },
      onDoubleClick() {
        this.$refs.searchInput.select();
      },
      onFocus() {
        this.isInputFocused = true;
        this.isNavigating = false;
        if (this.searchInput) return;
        this.loadDefaultResults();
      },
      onBlur(event) {
        if (this.isNavigating) {
          return;
        }
        const relatedTarget = event.relatedTarget || document.activeElement;
        const searchFlydown = this.$el.querySelector('.search-flydown');
        if (relatedTarget && searchFlydown && searchFlydown.contains(relatedTarget)) {
          return;
        }
        setTimeout(() => {
          if (document.activeElement !== this.$refs.searchInput && !this.isNavigating) {
            this.isInputFocused = false;
          }
        }, 150);
      },
      onLinkMouseDown() {
        this.isNavigating = true;
      },
      onLinkClick() {
        setTimeout(() => {
          this.isNavigating = false;
        }, 300);
      },
      onProductClick(product) {
        this.onLinkClick();
        this.trackFastSimon(product);
      }
    }
  };
</script>

<template>
  <div class="live-search-form form-fields-inline" method="get" role="search" aria-label="Product">
    <div class="form-field no-label">
      <input class="form-field-input live-search-form-field gt-p" type="text" name="q" aria-label="Search" :placeholder="this.placeHolder" autocomplete="off" @input="debouncedOnChanger" @compositionend="debouncedOnChanger" v-model="searchInput" @keydown.enter.prevent.stop="submitSearch" @touchstart="onTouchStart" @click="onInputClick" @focus="onFocus" @blur="onBlur" @dblclick="onDoubleClick" ref="searchInput" />
      <button type="button" @click.prevent="submitSearch" class="live-search-button" aria-label="Search">
        <span class="search-icon search-icon--inactive">
          <IconSearch />
        </span>
      </button>

      <div class="search__predictive__clear" @click="clearSearchResults" v-if="!!searchInput">
        <button type="reset" class="search__predictive__close__inner search-reset" aria-label="Reset">Clear</button>
      </div>

      <div class="search__predictive__close">
        <button type="button" class="search__predictive__close__inner" data-close-popdown="" aria-label="Close" @click="closeSearchResults">
          <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 15 15">
            <g fill-rule="evenodd">
              <path d="m2.06 1 12.02 12.021-1.06 1.061L1 2.062z"></path>
              <path d="M1 13.02 13.022 1l1.061 1.06-12.02 12.02z"></path>
            </g>
          </svg>
        </button>
      </div>
    </div>

    <div class="search-flydown" v-show="(defaultQueryEnabled || searchInput) && (!isMobile || isInputFocused || !!searchInput || defaultQueryEnabled)">
      <div class="suggestion-wrapper">
        <!-- Suggestions Results -->
        <div class="search-flydown--content-items search-suggestions">
          <div v-if="suggestions && suggestions.length > 0">
            <div class="fs_autocomplete_links_title">Suggestions:</div>
            <ul class="search-flydown--content-items-list">
              <li class="search-flydown--content-item" v-for="(suggestion, suggestionIndex) in suggestions" :key="suggestionIndex">
                <a @mousedown="onLinkMouseDown" @touchstart="onLinkMouseDown" @click.prevent="submitSearch($event, typeof suggestion === 'string' ? suggestion : suggestion.label)" :href="getSearchLink(typeof suggestion === 'string' ? suggestion : suggestion.label)" v-html="typeof suggestion === 'string' ? suggestion : suggestion.label"></a>
              </li>
            </ul>
          </div>
        </div>
        <a v-if="searchInput && searchInput.length > 0" class="see-all-results" :href="getSearchLink(searchInput)" @mousedown="onLinkMouseDown" @touchstart="onLinkMouseDown" @click.prevent="submitSearch" v-html="mobileSearchSeeAll"> </a>

        <!-- Category Results -->
        <div class="search-flydown--content-items search-categories">
          <div v-if="categories && categories.length > 0">
            <div class="fs_autocomplete_links_title">Categories:</div>
            <ul class="search-flydown--content-items-list">
              <li class="search-flydown--content-item" v-for="(category, categoryIndex) in categories" :key="categoryIndex">
                <a 
                @mousedown="onLinkMouseDown" 
                @touchstart="onLinkMouseDown" 
                @click="onLinkClick" 
                :href="getCurrentLocales + (typeof category === 'string' ? '/collections/' + (categoryHandles[categoryIndex] || '') : category && category.u ? category.u : '')" 
                :class="{ capitalize: typeof category === 'string' }" 
                v-text="getCategoryDisplayLabel(category, categoryIndex)"></a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Product Results -->
      <div class="search-flydown--content-items search-products">
        <div class="search-flydown--product-items" v-for="(product, index) in searchResults.products" :key="index">
          <a class="search-flydown--product" @mousedown="onLinkMouseDown" @touchstart="onLinkMouseDown" @click="onProductClick(product)" :href="getCurrentLocales + product.u">
            <div class="search-flydown--product-image">
              <img :src="product.t" :alt="product.l" />
            </div>
            <div class="search-flydown--product-text">
              <div v-html="setPriceDisplay(product.l, product.p_c, product.p)"></div>
            </div>
          </a>
        </div>
      </div>
    </div>

    <!-- Footer Results -->
    <div class="search-flydown--footer-wrapper">
      <div class="search-flydown--footer" v-if="searchResults?.products?.length > 0 && !!searchInput">
        <a class="see-all-results gt-btn btn--outline btn--full btn--primary" :href="getSearchLink(searchInput)" @mousedown="onLinkMouseDown" @touchstart="onLinkMouseDown" @click.prevent="submitSearch" v-html="'See all results'"> </a>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .live-search-fs {
    .is-mobile & {
      .search__predictive__close {
        height: unset;
      }
    }

    .search__predictive__close__inner.search-reset {
      text-decoration: underline;
    }

    .suggestion-wrapper {
      display: flex;
      flex-direction: column;
      flex-basis: 25%;

      .is-mobile & {
        flex-basis: 100%;
        width: 100%;
      }

      @media (max-width: 767px) {
        flex-basis: 100%;
        width: 100%;
      }
    }

    .form-field.no-label {
      display: flex;
      align-items: center;
      margin: 10px 0 10px 0;
      padding-left: 8px;
      position: relative;
      z-index: 1;
    }

    .live-search-form-field {
      border: 0;
      margin-left: 8px;
      margin-right: 30px;
      margin-bottom: 0;
      flex: 1;
      touch-action: manipulation;
      -webkit-user-select: text;
      user-select: text;
      -webkit-touch-callout: default;
      -webkit-tap-highlight-color: transparent;
      cursor: text;

      .is-mobile & {
        margin-right: 0;
      }

      @media (max-width: 767px) {
        margin-right: 0;
      }
    }

    .live-search-form-field:focus {
      box-shadow: none;
    }

    .is-mobile & {
      .live-search-form-field {
        min-height: 44px; // iOS minimum touch target size
        padding: 8px 0 8px 8px;
        border-radius: 0;
        -webkit-appearance: none;
        appearance: none;
        font-size: max(16px, var(--live-search-input-font-size, 16px)) !important;
      }
    }

    @media (max-width: 767px) {
      .live-search-form-field {
        min-height: 44px;
        padding: 8px 0 8px 8px;
        border-radius: 0;
        -webkit-appearance: none;
        appearance: none;
        font-size: max(16px, var(--live-search-input-font-size, 16px)) !important;
      }
    }

    .live-search-button {
      position: static;
      flex-shrink: 0;
      order: -1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      margin: 0;

      .search-icon {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      svg {
        width: 15px;
        height: auto;
      }
    }

    .search-flydown {
      display: flex;

      .is-mobile & {
        flex-direction: column;
      }

      @media (max-width: 767px) {
        flex-direction: column;
      }
    }

    .search-products {
      flex: 1;
      min-width: 0;

      .is-mobile & {
        width: 100%;
        flex: none;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: center;
      }

      @media (max-width: 767px) {
        width: 100%;
        flex: none;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: center;
      }
    }

    .search-flydown--content-items {
      display: flex;
    }

    .search-flydown--product-items {
      flex-basis: 25%;
      max-width: 25%;
      padding-left: 16px;

      .is-mobile & {
        flex-basis: calc(50% - 8px);
        max-width: calc(50% - 8px);
        padding-left: 0;
        margin: 0 4px 16px;
      }

      @media (max-width: 767px) {
        flex-basis: calc(50% - 8px);
        max-width: calc(50% - 8px);
        padding-left: 0;
        margin: 0 4px 16px;
      }
    }

    .search-categories,
    .search-suggestions {
      margin-bottom: 20px;

      div {
        width: 100%;
      }
    }

    .search-flydown--product-image {
      img {
        width: 100%;
        aspect-ratio: var(--PRODUCT-GRID-ASPECT-RATIO-DECIMAL, 1);
        object-fit: var(--PRODUCT-GRID-OBJECT-FIT, contain);
        background: var(--PRODUCT-GRID-BACKGROUND-COLOR, transparent);

        .is-mobile & {
          object-fit: cover;
        }

        @media (max-width: 767px) {
          object-fit: cover;
        }
      }
    }

    .search-flydown--product-text {
      .price.on-sale {
        margin-right: 8px;
      }

      .compare-at {
        margin-left: 0;
      }

      .percentage-off {
        margin-left: 8px;
      }
    }

    .search-flydown--footer-wrapper {
      margin-bottom: 20px;
      text-align: center;

      .search-flydown--footer {
        margin-top: 50px;
      }
    }

    .see-all-results {
      max-width: 300px;
      color: var(--text);
    }

    .is-tablet &,
    .is-desktop & {
      .suggestion-wrapper .see-all-results {
        display: none;
      }

      .see-all-results,
      .see-all-results:hover {
        color: var(--SEARCH-PREVIEW-BTN-TEXT);
        background: var(--SEARCH-PREVIEW-BTN-BG);
      }
    }

    .fs_autocomplete_links_title {
      margin-right: 0.5em;
      margin-bottom: 4px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid var(--border);
      margin-bottom: var(--gutter);
    }

    .is-mobile & {
      .search-flydown {
        margin-top: 10px;
      }

      .search-flydown--footer-wrapper {
        margin-bottom: 50px;
        display: none;
      }

      .fs_autocomplete_links_title {
        margin-bottom: 4px;
      }

      .search-flydown--content-items-list {
        margin-bottom: 0;
      }

      .search-flydown--content-item:last-child {
        margin-bottom: 0;
      }

      .search-categories {
        margin-bottom: 24px;
      }

      .see-all-results {
        margin-top: 0;
        margin-bottom: 20px;
      }
    }

    .search-flydown a strong {
      font-family: var(--GT-P-BOLD);
      font-weight: var(--GT-P-BOLD-WEIGHT);
    }

    .price {
      font-size: smaller;
      font-weight: 400;
    }

    .price.on-sale {
      margin-right: 7px;
      color: var(--COLOR-SALE-TEXT);
      text-decoration: none;
    }

    .compare-at {
      text-decoration: line-through !important;
    }
    .capitalize {
      text-transform: capitalize;
    }
    .percentage-off {
      color: var(--COLOR-SALE-TEXT);
      text-decoration: none;
    }
  }
</style>
